import { useEffect, useRef, useState } from "react";
import { IconArrowsMinimize, IconMinus, IconPlus } from "@tabler/icons-react";
import { ViewerToolbarButton, ViewerToolbarDivider } from "./ViewerToolbar";

// Zoom against the frame at its own size: 1 shows the page as it fits, 4 is
// as close as it goes. A click on − or + moves by one step.
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const STEP = 1.25;

type Size = { width: number; height: number };

/**
 * An embedded page the reader zooms (a Figma Site, our own HTML page): the
 * page is cross-origin, so its content cannot be zoomed from here. The frame
 * keeps the size of its slot, so the page keeps its layout, and is scaled as
 * a whole with a transform, like an image. Zoomed, the slot scrolls to move
 * around it; the page still scrolls inside itself.
 *
 * A UI primitive: the zoom and the measured slot are machinery. It fills its
 * parent; the caller keys it on the url, so the next page opens fitted.
 */
export function ZoomableFrame({
  zoomOutLabel,
  zoomInLabel,
  fitLabel,
  toolbarEnd,
  children,
}: {
  zoomOutLabel: string;
  zoomInLabel: string;
  fitLabel: string;
  /** The caller's own controls, after the zoom in the bar. */
  toolbarEnd: React.ReactNode;
  /** The frame (an iframe), drawn at the slot's size. */
  children: React.ReactNode;
}): React.JSX.Element {
  const slotRef = useRef<HTMLDivElement>(null);
  const [slot, setSlot] = useState<Size | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);

  // The frame is drawn at the slot's size, measured: a resized window resizes
  // the page too, as it would without the zoom.
  useEffect(() => {
    const element = slotRef.current;
    if (element === null) {
      throw new Error("should not happen: no slot after mount");
    }
    const observer = new ResizeObserver(() => {
      setSlot({ width: element.clientWidth, height: element.clientHeight });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Zooms on the centre of what is on screen: the slot's scroll follows.
  function zoomTo(next: number): void {
    const element = slotRef.current;
    if (element === null) {
      throw new Error("should not happen: a zoom before the slot is laid out");
    }
    const target = Math.min(Math.max(next, MIN_ZOOM), MAX_ZOOM);
    const ratio = target / zoom;
    const centreX = element.scrollLeft + element.clientWidth / 2;
    const centreY = element.scrollTop + element.clientHeight / 2;
    setZoom(target);
    requestAnimationFrame(() => {
      element.scrollLeft = centreX * ratio - element.clientWidth / 2;
      element.scrollTop = centreY * ratio - element.clientHeight / 2;
    });
  }

  const isZoomed = zoom > MIN_ZOOM;

  return (
    <div className="relative size-full">
      <div ref={slotRef} className="size-full overflow-auto">
        {slot === null ? null : (
          <div
            style={{
              width: slot.width * zoom,
              height: slot.height * zoom,
            }}
          >
            <div
              className="origin-top-left"
              style={{
                width: slot.width,
                height: slot.height,
                transform: `scale(${String(zoom)})`,
              }}
            >
              {children}
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-ds-slate-100 bg-ds-white p-1 shadow-popover">
        <ViewerToolbarButton
          label={zoomOutLabel}
          isDisabled={!isZoomed}
          onClick={() => {
            zoomTo(zoom / STEP);
          }}
        >
          <IconMinus stroke={1.5} />
        </ViewerToolbarButton>
        <span className="min-w-14 text-center text-footnote font-semibold text-ds-black">
          {`${String(Math.round(zoom * 100))} %`}
        </span>
        <ViewerToolbarButton
          label={zoomInLabel}
          isDisabled={zoom >= MAX_ZOOM}
          onClick={() => {
            zoomTo(zoom * STEP);
          }}
        >
          <IconPlus stroke={1.5} />
        </ViewerToolbarButton>
        <ViewerToolbarDivider />
        <ViewerToolbarButton
          label={fitLabel}
          isDisabled={!isZoomed}
          onClick={() => {
            zoomTo(MIN_ZOOM);
          }}
        >
          <IconArrowsMinimize stroke={1.5} />
        </ViewerToolbarButton>
        {toolbarEnd}
      </div>
    </div>
  );
}
